import React, { useRef, useState, useCallback } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import {
  type Feature as FeatureObject,
  FeatureType,
} from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { Loader } from '@/app/components/search/components/Card/Loaders/styled.ts';
import { useReopenGuard } from '@/app/common/hooks/useReopenGuard.ts';
import { IconOrLegacyAsset } from '@/app/common/icons/IconOrLegacyAsset';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { FeatureContainer, FeatureIcon, FeatureLink, FeatureText } from './styled';
import { IconWithTooltip, TooltipContent } from '../../../../Tooltip/styled';
import { TooltipType } from '../../../../Tooltip/TooltipType';
import useTooltipShowOnBottom from '../../../../../hooks/useTooltipShowOnBottom';

type FeatureProps = FeatureObject & {
  index?: number;
  length?: number;
  testId?: string;
};

const DEFAULT_FEATURE_ICON = { category: 'Semantics', name: 'icon-check', color: 'iconPrimary' } as const;

const BRAND_TINTED_FEATURES: FeatureType[] = [FeatureType.primary, FeatureType.offer];
const tintIcon = <T extends StarfishIconRef | string | undefined>(featureType: FeatureType | undefined, icon: T): T =>
  icon && typeof icon === 'object' && featureType && BRAND_TINTED_FEATURES.includes(featureType)
    ? ({ ...icon, color: 'iconPrimaryDefault' } as T)
    : icon;

const isTravelExtraPack = (className?: string) => className?.includes('travel-extra-pack');
const isAlternativeFlexibleDates = (className?: string) => className?.includes('dates-more');

const Feature = ({
  icon,
  text,
  featureType,
  iconRight,
  tooltip,
  Modal,
  tooltipSize,
  tooltipPosition,
  index,
  length,
  className,
  testId,
  link,
  callback,
}: FeatureProps) => {
  const isClient = useIsClient();
  const { localization } = usePageWrapperData();
  const { isMobile, isDesktop } = useCommonClient();
  const { curiosityClickEvents } = useCuriosityManager();
  const featureRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Prevents the trigger button's click event (which follows pointerdown on the backdrop)
  // from immediately reopening the modal in the same pointer interaction.
  const reopenGuard = useReopenGuard();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipEnabled = tooltipSize && iconRight;
  const hasIcon = icon !== '';
  const waiting = featureType === FeatureType.waiting;
  const isFirst = length ? index === 0 : false;
  const isLast = length ? index === length - 1 : false;
  const { showOnBottom } = useTooltipShowOnBottom(tooltipRef, isOpen, isMobile);

  const handleTooltipClick = () => {
    if (!isDesktop) {
      setIsOpen(true);
    }
  };
  const handleOnModalClick = useCallback(() => {
    if (reopenGuard.blocked()) return;
    if (isAlternativeFlexibleDates(className)) {
      curiosityClickEvents.alternativeFlexibleDatesOpenModal();
    }
    if (isTravelExtraPack(className)) {
      curiosityClickEvents.travelExtraPackOpenModal();
    }

    setIsOpen(true);
  }, [className, curiosityClickEvents, reopenGuard]);

  const handleOnModalClose = useCallback(
    (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (isAlternativeFlexibleDates(className)) {
        curiosityClickEvents.alternativeFlexibleDatesCloseModal();
      }
      if (isTravelExtraPack(className)) {
        curiosityClickEvents.travelExtraPackCloseModal();
      }

      e?.preventDefault();
      e?.stopPropagation();
      reopenGuard.markClosed();
      setIsOpen(false);
    },
    [className, curiosityClickEvents, reopenGuard]
  );

  const handleOnMouseEnter = () => {
    if (isDesktop) {
      setIsOpen(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (isDesktop) {
      setIsOpen(false);
    }
  };

  const handleLinkClicked = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (link) link();
  };

  useOnclickOutside(
    () => {
      if (tooltip && isOpen && !isDesktop) {
        setIsOpen(false);
      }
    },
    { refs: tooltipRef.current ? [{ current: tooltipRef.current }] : [] }
  );

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Radix portals synthetic click events from the overlay through the React tree,
    // which can cause the modal to reopen immediately after the backdrop is tapped.
    // Guard by checking that the real DOM target is inside this button.
    if (featureRef.current && !featureRef.current.contains(e.nativeEvent.target as Node)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (callback || Modal || tooltip) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (callback) {
      callback();
      return;
    }
    if (Modal) {
      handleOnModalClick();
      return;
    }
    if (tooltip) {
      handleTooltipClick();
    }
  };

  const isInteractive = !!(callback || Modal || tooltip);
  const cleanText = text?.replace(/<[^>]*>/g, '');

  const containerProps = {
    ref: featureRef,
    featureType,
    isLast,
    localization,
    className,
    'data-testid': testId,
    onClick: handleOnClick,
    isInteractive,
    ...(isInteractive
      ? {
          type: 'button' as const,
          ...(cleanText && { 'aria-label': cleanText }),
          tabIndex: 0,
        }
      : {
          as: 'div' as const,
          disabled: true,
          tabIndex: -1,
        }),
  };

  const tooltipTemplate = tooltipEnabled && (
    <IconWithTooltip
      tooltipSize={tooltipSize}
      tooltipType={TooltipType.primary}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      {...(tooltipPosition ? { position: tooltipPosition } : {})}
    >
      <IconOrLegacyAsset icon={tintIcon(featureType, iconRight)} className="icon-right" size={16} />
      {tooltip && isOpen && (
        <TooltipContent ref={tooltipRef} tooltipSize={tooltipSize} className="tooltiptext" showOnBottom={showOnBottom}>
          {tooltip}
        </TooltipContent>
      )}
    </IconWithTooltip>
  );

  const renderFeatureText = () => {
    if (!text) return null;
    const isGray = featureType === FeatureType.gray;

    if (tooltipEnabled) {
      return (
        <FeatureText
          className="text"
          variant="BodySmall"
          forceTag="div"
          color={isGray ? 'textSecondary' : 'textPrimary'}
        >
          <AddElementToLastWordInString text={text}>{tooltipTemplate}</AddElementToLastWordInString>
        </FeatureText>
      );
    }

    if (iconRight) {
      return (
        <FeatureText
          className="text"
          variant="BodySmall"
          forceTag="div"
          color={isGray ? 'textSecondary' : 'textPrimary'}
        >
          <AddElementToLastWordInString text={text}>
            <IconOrLegacyAsset icon={tintIcon(featureType, iconRight)} className="icon-right" size={16} />
          </AddElementToLastWordInString>
        </FeatureText>
      );
    }

    return (
      <FeatureText
        className="text"
        variant="BodySmall"
        forceTag="div"
        dangerouslySetInnerHTML={{ __html: text }}
        color={isGray ? 'textSecondary' : 'textPrimary'}
      />
    );
  };

  if (waiting) {
    return (
      <FeatureContainer as="button" variant="BodySmall" {...containerProps}>
        <Loader elWidth={isFirst ? 180 : 205} elHeight={20} className={className} />
      </FeatureContainer>
    );
  }

  if (!text) return null;

  if (tooltipEnabled) {
    return (
      <FeatureContainer as="button" variant="BodySmall" {...containerProps} onClick={handleOnClick}>
        <FeatureIcon>
          <IconOrLegacyAsset icon={tintIcon(featureType, icon || DEFAULT_FEATURE_ICON)} size={16} />
        </FeatureIcon>
        {renderFeatureText()}
        {isClient && Modal && <Modal isOpen={isOpen} onClose={handleOnModalClose} />}
      </FeatureContainer>
    );
  }

  if (link) {
    return (
      <FeatureLink
        {...containerProps}
        onClick={handleLinkClicked}
        type="button"
        {...(cleanText && { 'aria-label': cleanText })}
      >
        {hasIcon && (
          <FeatureIcon>
            <IconOrLegacyAsset icon={tintIcon(featureType, icon || DEFAULT_FEATURE_ICON)} size={16} />
          </FeatureIcon>
        )}
        {renderFeatureText()}
        {isClient && Modal && <Modal isOpen={isOpen} onClose={handleOnModalClose} />}
      </FeatureLink>
    );
  }

  return (
    <FeatureContainer as="button" variant="BodySmall" {...containerProps}>
      <>
        {hasIcon && (
          <FeatureIcon>
            <IconOrLegacyAsset icon={tintIcon(featureType, icon || DEFAULT_FEATURE_ICON)} size={16} />
          </FeatureIcon>
        )}
        {renderFeatureText()}
        {isClient && Modal && <Modal isOpen={isOpen} onClose={handleOnModalClose} />}
      </>
    </FeatureContainer>
  );
};

export default Feature;
